// src/pages/AddKeychainOptionPage.tsx
import React, { useEffect, useState, useMemo } from "react";
import { apiUrl } from "../config/api";

/** ---------- Image resize helpers (กันรูปใหญ่) ---------- */
type ResizeOpts = {
  maxWidth?: number;
  maxHeight?: number;
  mimeType?: "image/jpeg" | "image/webp" | "image/png";
  quality?: number;        // 0..1
  targetMaxBytes?: number; // เช่น 1.5MB
};

async function fileToImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    await new Promise<void>((resolve, reject) => {
      const probe = new Image();
      probe.onload = () => resolve();
      probe.onerror = reject;
      probe.src = url;
    });
    const img = new Image();
    img.src = url;
    return img;
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

async function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob_failed"))), type, quality);
  });
}

/** ย่อรูป + บีบอัดคุณภาพไม่ให้เกิน targetMaxBytes */
async function resizeImage(
  file: File,
  opts: ResizeOpts = {}
): Promise<{ blob: Blob; file: File; previewUrl: string }> {
  const {
    maxWidth = 1280,
    maxHeight = 1280,
    mimeType = "image/jpeg",
    quality = 0.85,
    targetMaxBytes,
  } = opts;

  // แปลง HEIC/HEIF -> JPEG
  const outType = /heic|heif/i.test(file.type) ? "image/jpeg" : mimeType;

  const img = await fileToImage(file);

  let { width, height } = img;
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
  width = Math.round(width * ratio);
  height = Math.round(height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, width, height);

  let q = quality;
  let blob = await canvasToBlob(canvas, outType, q);

  if (targetMaxBytes && blob.size > targetMaxBytes) {
    while (q > 0.5 && blob.size > targetMaxBytes) {
      q -= 0.07;
      blob = await canvasToBlob(canvas, outType, q);
    }
  }

  const newName =
    file.name.replace(/\.(heic|heif|webp|png|jpg|jpeg)$/i, "") +
    (outType === "image/webp" ? ".webp" : ".jpg");

  const resizedFile = new File([blob], newName, { type: outType });
  const previewUrl = URL.createObjectURL(blob);

  return { blob, file: resizedFile, previewUrl };
}

type KeychainOptionRow = {
  id: number;
  label: string;
  extra_cost: number;
  imageUrl?: string;   // ⭐️ เพิ่มฟิลด์รูป
  category: "KEYCHAIN" | "ACCESSORY"; 
  last_unit_cost?: number | null;
  stock_on_hand: number;
};

export default function AddKeychainOptionPage(): JSX.Element {
  const [label, setLabel] = useState("");
  const [extraCost, setExtraCost] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [category, setCategory] = useState<"KEYCHAIN" | "ACCESSORY">("KEYCHAIN");

  const [options, setOptions] = useState<KeychainOptionRow[]>([]);
  const [filter, setFilter] = useState<"ALL" | "KEYCHAIN" | "ACCESSORY">("ALL");
  const [search, setSearch] = useState<string>(""); 

  const [loadingList, setLoadingList] = useState(false);
  const [listErr, setListErr] = useState("");


  const [editing, setEditing] = useState<KeychainOptionRow | null>(null);
  const [editCategory, setEditCategory] =
  useState<"KEYCHAIN" | "ACCESSORY">("KEYCHAIN");
  const [editLabel, setEditLabel] = useState<string>("");
  const [editFile, setEditFile] = useState<File | null>(null);
  const [editPreview, setEditPreview] = useState<string>("");
  const [editStatus, setEditStatus] = useState<{
    type: "success" | "error" | "info" | "";
    msg: string;
    }>({ type: "", msg: "" });
  const [editSaving, setEditSaving] = useState(false);

  const onPickEditImage = async (f: File | null): Promise<void> => {
    setEditStatus({ type: "", msg: "" });
  
    if (!f) {
      setEditFile(null);
      setEditPreview("");
      return;
    }
  
    try {
      const { file: resized, previewUrl } = await resizeImage(f, {
        maxWidth: 1280,
        maxHeight: 1280,
        mimeType: "image/jpeg",
        quality: 0.85,
        targetMaxBytes: 1.5 * 1024 * 1024,
      });
      setEditFile(resized);
      setEditPreview(previewUrl);
    } catch (e) {
      setEditFile(f);
      setEditPreview(URL.createObjectURL(f));
      setEditStatus({
        type: "info",
        msg: "ย่อรูปไม่สำเร็จ ใช้ไฟล์เดิมแทน",
      });
    }
  };

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<{
    type: "success" | "error" | "info" | "";
    msg: string;
  }>({ type: "", msg: "" });
  const [uploading, setUploading] = useState(false);

  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    tone: "success" | "error";
  }>({
    show: false,
    message: "",
    tone: "success",
  });

  const onPickImage = async (f: File | null): Promise<void> => {
    setUploadStatus({ type: "", msg: "" });
  
    if (!f) {
      setFile(null);
      setPreview("");
      return;
    }
  
    try {
      const { file: resized, previewUrl } = await resizeImage(f, {
        maxWidth: 1280,
        maxHeight: 1280,
        mimeType: "image/jpeg",
        quality: 0.85,
        targetMaxBytes: 1.5 * 1024 * 1024, // 1.5MB
      });
      setFile(resized);
      setPreview(previewUrl);
    } catch (e) {
      // ถ้าย่อไม่สำเร็จ ใช้ไฟล์เดิม
      setFile(f);
      setPreview(URL.createObjectURL(f));
      setUploadStatus({
        type: "info",
        msg: "ย่อรูปไม่สำเร็จ ใช้ไฟล์เดิมแทน",
      });
    }
  };

  const uploadKeychainImage = async (id: number): Promise<void> => {
    if (!file) return;
    try {
      setUploading(true);
      setUploadStatus({ type: "info", msg: "กำลังอัปโหลดรูป..." });

      const fd = new FormData();
      fd.append("file", file);

      const resp = await fetch(
        apiUrl(`/upload/keychain/${encodeURIComponent(String(id))}`),
        {
          method: "POST",
          body: fd,
        }
      );
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || j?.ok === false) {
        throw new Error(j?.error ?? resp.statusText);
      }

      setUploadStatus({ type: "success", msg: "อัปโหลดรูปสำเร็จ ✅" });
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "อัปโหลดรูปไม่สำเร็จ กรุณาลองใหม่";
      setUploadStatus({ type: "error", msg });
    } finally {
      setUploading(false);
    }
  };

  const showToast = (message: string, tone: "success" | "error" = "success") => {
    setToast({ show: true, message, tone });
    setTimeout(() => setToast({ show: false, message: "", tone: "success" }), 1800);
  };

  // PMN-CRITICAL(/AddKeychainOptionPage): Option list combines keychain_options master data with keychain_movements stock/cost; do not treat options as stock lots.
  // โหลดรายการ keychain options
  const loadOptions = async (): Promise<void> => {
    setLoadingList(true);
    setListErr("");
    try {
      const resp = await fetch(apiUrl("/keychain_options_list"));
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error ?? resp.statusText);
  
      const rawItems = Array.isArray(j?.items) ? j.items : [];
      const items: KeychainOptionRow[] = rawItems.map((r: any) => ({
        id: Number(r.id),
        label: String(r.label ?? ""),
        extra_cost: Number(r.extra_cost ?? r.extraCost ?? 0),
        imageUrl: r.imageUrl ?? r.image_url ?? undefined,
        category:
          (r.category === "ACCESSORY" ? "ACCESSORY" : "KEYCHAIN") as
            | "KEYCHAIN"
            | "ACCESSORY",
        last_unit_cost:
          r.last_unit_cost !== undefined && r.last_unit_cost !== null
            ? Number(r.last_unit_cost)
            : undefined,
        stock_on_hand: Number(r.stock_on_hand ?? 0),   // 👈 map เข้ามา
      }));
      setOptions(items);
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "โหลดรายการพวงกุญแจไม่สำเร็จ";
      setListErr(msg);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    void loadOptions();
  }, []);

  const filteredOptions = useMemo(() => {
    let list = options;

    if (filter !== "ALL") {
        list = list.filter((o) => o.category === filter);
    }

    const q = search.trim().toLowerCase();
    if (q) {
        list = list.filter((o) => o.label.toLowerCase().includes(q));
    }

    return list;
  }, [options, filter, search]);

  // PMN-CRITICAL(/AddKeychainOptionPage): Create inserts the option before image upload, so upload failure can leave a valid option without an image.
  const save = async (): Promise<void> => {
    if (!label.trim()) {
      setErr("กรุณากรอกชื่อ/คำอธิบายพวงกุญแจ");
      return;
    }
    if (extraCost < 0) {
      setErr("ค่า extra cost ต้องไม่ติดลบ");
      return;
    }

    setLoading(true);
    setErr("");
    try {
      const resp = await fetch(apiUrl("/keychain_optionsAdd"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: label.trim(),
          extra_cost: extraCost,
          category,   
        }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || j?.ok !== true) throw new Error(j?.error ?? resp.statusText);

      const newId: number | undefined =
        typeof j?.id === "number" ? j.id : undefined;

      showToast("บันทึกพวงกุญแจสำเร็จ ✅", "success");

      // ถ้ามีรูป ให้ตามไปอัปโหลดต่อ
      if (file && newId) {
        await uploadKeychainImage(newId);
      }

      // ล้างฟอร์ม
      setLabel("");
      setExtraCost(0);
      setCategory("KEYCHAIN");  
      setFile(null);
      setPreview("");
      setUploadStatus({ type: "", msg: "" });

      await loadOptions();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "บันทึกไม่สำเร็จ";
      setErr(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  // PMN-CRITICAL(/AddKeychainOptionPage): Delete removes keychain_options master rows; verify stock/movement references before relying on this in production.
  const deleteOption = async (id: number): Promise<void> => {
    if (!window.confirm("ยืนยันลบตัวเลือกพวงกุญแจนี้?")) return;
  
    setLoadingList(true);
    setListErr("");
    try {
      const resp = await fetch(apiUrl(`/keychain_options/${id}`), {
        method: "DELETE",
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || j?.ok !== true) {
        throw new Error(j?.error ?? resp.statusText);
      }
  
      showToast("ลบตัวเลือกพวงกุญแจแล้ว", "success");
      await loadOptions();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "ลบไม่สำเร็จ";
      setListErr(msg);
      showToast(msg, "error");
    } finally {
      setLoadingList(false);
    }
  };

  const openEdit = (opt: KeychainOptionRow): void => {
    setEditing(opt);
    setEditLabel(opt.label);
    setEditFile(null);
    setEditPreview(opt.imageUrl ?? "");
    setEditStatus({ type: "", msg: "" });
    setEditCategory(opt.category); 
  };
  
  const closeEdit = (): void => {
    setEditing(null);
    setEditLabel("");
    setEditFile(null);
    setEditPreview("");
    setEditStatus({ type: "", msg: "" });
    setEditCategory("KEYCHAIN");
  };
  
  // PMN-CRITICAL(/AddKeychainOptionPage): Edit updates label/category and optionally image, but does not update extra_cost.
  const saveEdit = async (): Promise<void> => {
    if (!editing) return;
    const id = editing.id;
    const newLabel = editLabel.trim();
    if (!newLabel) {
      setEditStatus({ type: "error", msg: "กรุณากรอกชื่อพวงกุญแจ" });
      return;
    }
  
    setEditSaving(true);
    setEditStatus({ type: "", msg: "" });
    try {
      // 1) อัปเดตชื่อ
      const resp = await fetch(apiUrl(`/keychain_options/${id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: newLabel, category: editCategory, }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || j?.ok === false) {
        throw new Error(j?.error ?? resp.statusText);
      }
  
      // 2) ถ้ามีรูปใหม่ ให้ตามไปอัปโหลด
      if (editFile) {
        const fd = new FormData();
        fd.append("file", editFile);
  
        const upResp = await fetch(
          apiUrl(`/upload/keychain/${encodeURIComponent(String(id))}`),
          { method: "POST", body: fd }
        );
        const upJ = await upResp.json().catch(() => ({}));
        if (!upResp.ok || upJ?.ok === false) {
          throw new Error(upJ?.error ?? upResp.statusText);
        }
      }
  
      showToast("แก้ไขตัวเลือกพวงกุญแจแล้ว ✅", "success");
      await loadOptions();
      closeEdit();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "แก้ไขไม่สำเร็จ";
      setEditStatus({ type: "error", msg });
    } finally {
      setEditSaving(false);
    }
  };
  

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {/* Toast */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div
            className={`alert ${
              toast.tone === "error" ? "alert-danger" : "alert-success"
            } shadow mb-0 rounded-3 py-2 px-3`}
          >
            {toast.message}
          </div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <h4 className="mb-3 text-primary text-center">
                  ตั้งค่าพวงกุญแจ (Keychain Options)
                </h4>

                <div className="row g-4">
                  {/* ซ้าย: รายการที่มีอยู่ */}
                  <div className="col-12 col-lg-6">
                    <div className="card border-0 shadow-sm rounded-3">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="fw-semibold">รายการพวงกุญแจที่มีอยู่</div>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => void loadOptions()}
                                disabled={loadingList}
                            >
                                {loadingList ? "กำลังโหลด…" : "รีเฟรช"}
                            </button>
                            </div>

                            {/* ⭐️ แถบเลือกกลุ่ม */}
                            <div className="btn-group btn-group-sm mb-2" role="group">
                                <button
                                    type="button"
                                    className={`btn btn-outline-secondary ${
                                    filter === "ALL" ? "active" : ""
                                    }`}
                                    onClick={() => setFilter("ALL")}
                                >
                                    ทั้งหมด
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-outline-secondary ${
                                    filter === "KEYCHAIN" ? "active" : ""
                                    }`}
                                    onClick={() => setFilter("KEYCHAIN")}
                                >
                                    Keychains
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-outline-secondary ${
                                    filter === "ACCESSORY" ? "active" : ""
                                    }`}
                                    onClick={() => setFilter("ACCESSORY")}
                                >
                                    Accessories
                                </button>
                            </div>
                            {/* ⭐️ ช่องค้นหาในลิสต์ */}
                            <div className="input-group input-group-sm mb-2">
                                <span className="input-group-text">ค้นหา</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="พิมพ์ชื่อพวงกุญแจ..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>

                        {listErr && (
                          <div className="alert alert-warning py-2">{listErr}</div>
                        )}

                        <div
                          className="list-group"
                          style={{ maxHeight: 420, overflowY: "auto" }}
                        >
                          {filteredOptions.map((opt) => (
                          <div
                            key={opt.id}
                            className="list-group-item d-flex align-items-center justify-content-between"
                          >
                            <div className="d-flex align-items-center flex-grow-1 gap-2">
                              {/* 👇 เพิ่ม id ตรงนี้ */}
                              <span className="badge bg-secondary-subtle text-muted">
                                #{opt.id}
                              </span>

                              {opt.imageUrl ? (
                                <img
                                  src={opt.imageUrl}
                                  alt={opt.label}
                                  style={{
                                    width: 50,
                                    height: 50,
                                    objectFit: "cover",
                                    borderRadius: 8,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: 8,
                                    background: "#eee",
                                  }}
                                />
                              )}

                              <span className="text-truncate" title={opt.label}>
                                {opt.label}
                              </span>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                              <span
                                className="badge bg-light text-muted"
                                title="จำนวนคงเหลือในสต๊อกของตัวเลือกนี้"
                              >
                                {opt.stock_on_hand} ชิ้น
                              </span>

                              <span
                                className="badge bg-light text-muted"
                                title={
                                  opt.last_unit_cost != null
                                    ? "ราคาต่อชิ้นจากการรับเข้าล่าสุด (keychain_movements)"
                                    : "ยังไม่มี movement ใช้ค่า extra_cost แทน"
                                }
                              >
                                ฿{opt.last_unit_cost ?? opt.extra_cost.toFixed(2)}
                              </span>

                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => openEdit(opt)}
                              >
                                แก้ไข
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => void deleteOption(opt.id)}
                              >
                                ลบ
                              </button>
                            </div>
                          </div>
                        ))}
                            {/* ---------- Modal แก้ไขตัวเลือก ---------- */}
                            {editing && (
                            <div
                                className="modal fade show"
                                style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
                                aria-modal="true"
                                role="dialog"
                                onClick={(e) => { if (e.target === e.currentTarget) closeEdit(); }}
                            >
                                <div className="modal-dialog modal-md">
                                <div className="modal-content rounded-4 shadow">
                                    <div className="modal-header">
                                    <h5 className="modal-title">แก้ไขตัวเลือกพวงกุญแจ</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={closeEdit}
                                    />
                                    </div>

                                    <div className="modal-body">
                                    <div className="mb-2 small text-muted">
                                        ID: <code>{editing.id}</code>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">ชื่อ / คำอธิบาย</label>
                                        <input
                                        type="text"
                                        className="form-control"
                                        value={editLabel}
                                        onChange={(e) => setEditLabel(e.target.value)}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">กลุ่มตัวเลือก</label>
                                        <select
                                            className="form-select"
                                            value={editCategory}
                                            onChange={(e) =>
                                            setEditCategory(
                                                e.target.value === "ACCESSORY" ? "ACCESSORY" : "KEYCHAIN"
                                            )
                                            }
                                        >
                                            <option value="KEYCHAIN">กลุ่มพวงกุญแจ</option>
                                            <option value="ACCESSORY">กลุ่มเครื่องประดับ</option>
                                        </select>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">รูปตัวอย่าง (ออปชัน)</label>
                                        <div
                                          className="border rounded-3 p-3 bg-light text-center"
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            document.getElementById("keychain-edit-file-input")?.click()
                                          }
                                        >
                                          <input
                                            id="keychain-edit-file-input"
                                            type="file"
                                            accept="image/*"
                                            capture="environment"
                                            style={{ display: "none" }}
                                            onChange={(e) => {
                                              void onPickEditImage(e.target.files?.[0] ?? null);
                                            }}
                                          />
                                          {editPreview ? (
                                            <img
                                              src={editPreview}
                                              alt="preview"
                                              className="img-fluid"
                                              style={{ maxHeight: 180, borderRadius: 8 }}
                                            />
                                          ) : editing.imageUrl ? (
                                            <img
                                              src={editing.imageUrl}
                                              alt={editing.label}
                                              className="img-fluid"
                                              style={{ maxHeight: 180, borderRadius: 8 }}
                                            />
                                          ) : (
                                            <div className="text-muted small">
                                              คลิกเพื่อเลือกไฟล์รูป (ไม่บังคับ)
                                            </div>
                                          )}
                                        </div>

                                        {editStatus.msg && (
                                        <div
                                            className={`alert alert-${
                                            editStatus.type === "success"
                                                ? "success"
                                                : editStatus.type === "error"
                                                ? "danger"
                                                : "info"
                                            } mt-2 py-2`}
                                        >
                                            {editStatus.msg}
                                        </div>
                                        )}
                                    </div>
                                    </div>

                                    <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={closeEdit}
                                    >
                                        ยกเลิก
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={editSaving}
                                        onClick={() => void saveEdit()}
                                    >
                                        {editSaving ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
                                    </button>
                                    </div>
                                </div>
                                </div>
                            </div>
                            )}
                            {/* ---------- /Modal แก้ไขตัวเลือก ---------- */}
                          {!loadingList && options.length === 0 && (
                            <div className="list-group-item text-muted text-center">
                              ยังไม่มีตัวเลือกพวงกุญแจ
                            </div>
                          )}
                        </div>

                        <div className="small text-muted mt-2">
                            ทั้งหมด {filteredOptions.length.toLocaleString()} ตัวเลือก
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ขวา: ฟอร์มเพิ่ม option */}
                  <div className="col-12 col-lg-6">
                    <div className="card border-0 shadow-sm rounded-3">
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label">ชื่อ / คำอธิบายพวงกุญแจ</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder='เช่น "ห่วงเล็ก + โซ่สั้น"'
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                          />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">กลุ่มตัวเลือก</label>
                            <select
                                className="form-select"
                                value={category}
                                onChange={(e) =>
                                setCategory(e.target.value === "ACCESSORY" ? "ACCESSORY" : "KEYCHAIN")
                                }
                            >
                                <option value="KEYCHAIN">กลุ่มพวงกุญแจ</option>
                                <option value="ACCESSORY">กลุ่มเครื่องประดับ</option>
                            </select>
                        </div>

                        <div className="mb-3">
                          <label className="form-label">ต้นทุนเพิ่มต่อชิ้น (บาท)</label>
                          <input
                            type="number"
                            className="form-control"
                            min={0}
                            step={0.01}
                            value={extraCost}
                            onChange={(e) => setExtraCost(Number(e.target.value))}
                          />
                        </div>

                        {/* อัปโหลดรูป (ออปชัน) */}
                        <div className="mb-3">
                          <label className="form-label">รูปตัวอย่างพวงกุญแจ (ออปชัน)</label>
                          <div
                            className="border rounded-3 p-3 bg-light text-center"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              document.getElementById("keychain-file-input")?.click()
                            }
                          >
                            <input
                              id="keychain-file-input"
                              type="file"
                              accept="image/*"
                              capture="environment"
                              style={{ display: "none" }}
                              onChange={(e) => {
                                void onPickImage(e.target.files?.[0] ?? null);
                              }}
                            />
                            {preview ? (
                              <img
                                src={preview}
                                alt="preview"
                                className="img-fluid"
                                style={{ maxHeight: 180, borderRadius: 8 }}
                              />
                            ) : (
                              <div className="text-muted small">
                                คลิกเพื่อเลือกไฟล์รูป (ไม่บังคับ)
                              </div>
                            )}
                          </div>


                          {uploadStatus.msg && (
                            <div
                              className={`alert alert-${
                                uploadStatus.type === "success"
                                  ? "success"
                                  : uploadStatus.type === "error"
                                  ? "danger"
                                  : "info"
                              } mt-2 py-2`}
                            >
                              {uploadStatus.msg}
                            </div>
                          )}
                        </div>

                        {err && (
                          <div className="alert alert-warning py-2">{err}</div>
                        )}

                        <div className="d-flex justify-content-between">
                          <a
                            href="/"
                            className="btn btn-link text-decoration-none"
                          >
                            ← กลับหน้าเมนู
                          </a>
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={loading || !label.trim()}
                            onClick={() => void save()}
                          >
                            {loading ? "กำลังบันทึก..." : "บันทึกตัวเลือกพวงกุญแจ"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center small text-muted mt-3">
                  * ใช้ API: <code>POST /keychain_optionsAdd</code> และ{" "}
                  <code>GET /keychain_options_list</code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
